export interface ProjectChatUrlParams {
  /** The project (campaign) id to bind the chat to. */
  planId: string;
  /**
   * Resume a specific conversation in this project (the project's own
   * conversation list). Takes precedence over the "most-recently-touched"
   * resolution once it reaches the home composer.
   */
  resumeSessionId?: string | null;
  /** Workspace pin carried with a resumed conversation. */
  workspaceId?: string | null;
  /**
   * Start a genuinely-new thread bound to the plan (bypasses the
   * most-recently-touched resume). Threads `?new=true`.
   */
  isNew?: boolean;
  /**
   * Composer mode to open in. Defaults to `'plan'` for a fresh project-bound
   * chat (parity with the desktop header's historical `&mode=plan`); omitted
   * for a resume so the existing conversation loads without re-seeding a slash
   * command. Pass `null` to force no mode.
   */
  mode?: string | null;
}

/**
 * The single source of truth for the URL that opens the home composer bound to
 * a project. Every "New chat" entry point (desktop header, mobile FAB, rail
 * "+") and the rail conversation-resume rows go through here so the same label
 * never produces different behavior (the §2 "same label, different behavior"
 * break). Returns a path string ready for `navigate(...)`.
 */
export function buildProjectChatUrl(params: ProjectChatUrlParams): string {
  const { planId, resumeSessionId, workspaceId, isNew } = params;
  const mode = params.mode !== undefined ? params.mode : isNew ? 'plan' : null;

  const search = new URLSearchParams({ planId });
  if (mode) search.set('mode', mode);
  if (resumeSessionId) search.set('resumeSession', resumeSessionId);
  if (workspaceId) search.set('w', workspaceId);
  if (isNew) search.set('new', 'true');
  return `/?${search.toString()}`;
}
